import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  BASE_URL_SANDBOX,
  FULL_QC_LIST,
  BASE_URL_FOR_ELASTIC_SEARCH
} from '../../../Constants/Config';
import { waitForElasticConnection } from '../../../hooks/checkElasticConnection.js';
import { defaultTracyLatLng } from '../../../utils';
import Cookies from 'js-cookie';
import { createRequestCache } from '../../../utils/requestCache';

const brandsRequestCache = createRequestCache({
  ttlMs: 60 * 1000,
  maxEntries: 30,
});

const initialState = {
  loading: false,
  brandsList: [],
  error: '',
  lastFetched: null,
  lastFetchedStoreType: '',
  activeRequestId: null,
};

// Generate pending, fulfilled and rejected action types
export const fetchBrands = createAsyncThunk(
  'brands/fetchBrands',
  async (params = {}, { rejectWithValue, getState, signal }) => {
    try {
      const { forceRefresh = false } = params;
      const normalizedStoreType =
        params?.storeType === 'pickup' ? 'pickup' : params?.storeType || '';

      const storedLocation = Cookies.get('userLocation');
      let location = defaultTracyLatLng;
      try {
        location = storedLocation
          ? JSON.parse(decodeURIComponent(storedLocation))
          : defaultTracyLatLng;
      } catch (e) {
        location = defaultTracyLatLng;
      }

      const requestLat = location?.lat ?? '';
      const requestLong = location?.lng ?? '';
      const requestKey = JSON.stringify([
        BASE_URL_FOR_ELASTIC_SEARCH,
        BASE_URL_SANDBOX,
        FULL_QC_LIST,
        String(requestLat),
        String(requestLong),
        normalizedStoreType,
        'Brand',
        '0',
      ]);

      return await brandsRequestCache.getOrCreate(
        requestKey,
        async () => {
          const isElasticConnected = await waitForElasticConnection(getState);
          const baseUrl = isElasticConnected
            ? BASE_URL_FOR_ELASTIC_SEARCH
            : BASE_URL_SANDBOX;
          const formData = new FormData();
          formData.append('lat', requestLat);
          formData.append('long', requestLong);
          formData.append('store', normalizedStoreType);
          formData.append('type', 'Brand');
          formData.append('status', '0');

          const response = await axios.post(
            baseUrl + FULL_QC_LIST,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 10000,
            },
          );

          const data = response.data?.qc_data;
          if (!Array.isArray(data)) {
            return [];
          }

          return data.filter(
            (item) =>
              item.type === 'Brand' && String(item.show_main) === '1',
          );
        },
        { forceRefresh, signal },
      );
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message || 'Network error occurred'
      });
    }
  },
);

const brandsSlice = createSlice({
  name: 'brands',
  initialState,
  reducers: {
    // Action to clear brands data if needed
    clearBrandsData: (state) => {
      state.brandsList = [];
      state.error = '';
      state.lastFetched = null;
      state.lastFetchedStoreType = '';
      state.activeRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBrands.pending, (state, action) => {
      state.loading = true;
      state.activeRequestId = action.meta.requestId;
      state.brandsList = [];
    });
    builder.addCase(fetchBrands.fulfilled, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return;

      state.loading = false;
      state.activeRequestId = null;
      state.brandsList = action.payload || [];
      state.error = '';
      state.lastFetched = Date.now(); // Set timestamp when data was fetched
      state.lastFetchedStoreType = action.meta.arg?.storeType === 'pickup' ? 'pickup' : action.meta.arg?.storeType || '';
    });
    builder.addCase(fetchBrands.rejected, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return;

      state.loading = false;
      state.activeRequestId = null;
      if (!action.meta.aborted) {
        state.error =
          action.payload?.message ||
          action.error?.message ||
          'Failed to fetch brands';
      }
    });
  },
});

export const { clearBrandsData } = brandsSlice.actions;
export default brandsSlice.reducer;
